import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Printer, FileDown } from "lucide-react";
import { generateA4PDF } from "./pdf/A4PDFGenerator";
import { generateTicketPDF } from "./pdf/TicketPDFGenerator";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface PrintPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction: any;
  items: any[];
  payments: any[];
  client: any;
  seller: any;
  tenant: any;
}

export function PrintPreviewModal({
  open,
  onOpenChange,
  transaction,
  items,
  payments,
  client,
  seller,
  tenant,
}: PrintPreviewModalProps) {
  // Buscar dados completos do tenant
  const { data: tenantData } = useQuery({
    queryKey: ["tenant-for-print", tenant?.name],
    queryFn: async () => {
      if (!tenant?.name) return null;
      const { data, error } = await supabase
        .from("tenants")
        .select("*")
        .eq("name", tenant.name)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: open && !!tenant?.name,
  });

  // Buscar produtos
  const { data: products } = useQuery({
    queryKey: ["products-for-print"],
    queryFn: async () => {
      const productIds = items.map((item) => item.product_id).filter(Boolean);
      if (productIds.length === 0) return [];
      const { data, error } = await supabase
        .from("products")
        .select("*")
        .in("id", productIds);
      if (error) throw error;
      return data || [];
    },
    enabled: open && items.length > 0,
  });

  // Buscar métodos de pagamento
  const { data: paymentMethods } = useQuery({
    queryKey: ["payment-methods-for-print"],
    queryFn: async () => {
      const methodIds = payments.map((p) => p.payment_method_id).filter(Boolean);
      if (methodIds.length === 0) return [];
      const { data, error } = await supabase
        .from("payment_methods")
        .select("*")
        .in("id", methodIds);
      if (error) throw error;
      return data || [];
    },
    enabled: open && payments.length > 0,
  });

  // Buscar valores dos campos do formulário para cada item
  const { data: itemsWithFormValues } = useQuery({
    queryKey: ["items-form-values-for-print", transaction?.id, items.map(i => i.id).join(",")],
    queryFn: async () => {
      if (!transaction?.id || items.length === 0) return items;

      console.log("🚀 Iniciando busca de valores do formulário. Items recebidos:", items);
      console.log("🚀 Transaction ID:", transaction.id);

      const itemsWithValues = await Promise.all(
        items.map(async (item) => {
          console.log("🔍 Buscando valores do formulário para item:", item, "transaction:", transaction.id);
          
          // Verificar se o item tem id
          if (!item.id) {
            console.log("⚠️ Item não tem id:", item);
            return {
              ...item,
              form_values: undefined,
              form_fields: undefined,
            };
          }
          
          // Buscar submissões do formulário relacionadas a este transaction_item
          const { data: submissions, error: submissionsError } = await (supabase as any)
            .from("form_submissions")
            .select("id, form_id")
            .eq("entity_type", "transaction")
            .eq("entity_id", transaction.id)
            .eq("custom_identifier", item.id);

          console.log("📋 Submissões encontradas para item", item.id, ":", submissions, "erro:", submissionsError);

          if (!submissions || submissions.length === 0) {
            console.log("⚠️ Nenhuma submissão encontrada para item:", item.id);
            // Fallback: construir form_fields a partir do produto e usar form_values do item em memória
            const product = (products || []).find((p: any) => p.id === item.product_id);
            const formId = product?.form_id;
            if (!formId) {
              return {
                ...item,
                form_values: item.form_values,
                form_fields: undefined,
              };
            }
            const { data: formFieldsData } = await (supabase as any)
              .from("form_fields")
              .select("id, slug, nome, tipo")
              .eq("form_id", formId)
              .order("ordem", { ascending: true });
            const formFields = await Promise.all(
              (formFieldsData || []).map(async (field: any) => {
                if (["select", "multiselect", "radio", "checkbox", "Seleção Única", "Múltipla Seleção"].includes(field.tipo)) {
                  const { data: options } = await (supabase as any)
                    .from("form_field_options")
                    .select("id, valor, label")
                    .eq("field_id", field.id)
                    .order("ordem", { ascending: true });
                  return { ...field, form_field_options: options || [] };
                }
                return { ...field, form_field_options: [] };
              })
            );
            return {
              ...item,
              form_values: item.form_values,
              form_fields: formFields,
            };
          }

          // Buscar valores da submissão mais recente
          const latestSubmission = submissions[0];
          console.log("📝 Buscando valores para submissão:", latestSubmission.id);
          
          const { data: submissionValues, error: submissionValuesError } = await (supabase as any)
            .from("form_submission_values")
            .select(`
              valor,
              valor_json,
              field_id,
              form_fields(id, slug, nome, tipo)
            `)
            .eq("submission_id", latestSubmission.id);
          
          console.log("📝 Valores encontrados:", submissionValues, "erro:", submissionValuesError);

          // Buscar campos do formulário
          const { data: formFieldsData } = await (supabase as any)
            .from("form_fields")
            .select("id, slug, nome, tipo")
            .eq("form_id", latestSubmission.form_id)
            .order("ordem", { ascending: true });

          // Buscar opções para cada campo
          const formFields = await Promise.all(
            (formFieldsData || []).map(async (field: any) => {
              // Buscar opções apenas para campos que têm opções
              if (["select", "multiselect", "radio", "checkbox"].includes(field.tipo)) {
                const { data: options } = await (supabase as any)
                  .from("form_field_options")
                  .select("id, valor, label")
                  .eq("field_id", field.id)
                  .order("ordem", { ascending: true });
                
                return {
                  ...field,
                  form_field_options: options || [],
                };
              }
              return {
                ...field,
                form_field_options: [],
              };
            })
          );

          // Construir objeto form_values
          const formValues: Record<string, any> = {};
          if (submissionValues) {
            console.log("📝 Valores da submissão:", submissionValues);
            submissionValues.forEach((sv: any) => {
              const field = sv.form_fields;
              if (field && field.slug) {
                // Usar valor_json se disponível, senão valor
                formValues[field.slug] = sv.valor_json !== null ? sv.valor_json : sv.valor;
              }
            });
          }

          console.log("✅ Form values construído:", formValues);
          console.log("✅ Form fields:", formFields);
          console.log("✅ Item final:", {
            id: item.id,
            product_id: item.product_id,
            hasFormValues: Object.keys(formValues).length > 0,
            hasFormFields: formFields.length > 0
          });

          return {
            ...item,
            form_values: Object.keys(formValues).length > 0 ? formValues : undefined,
            form_fields: formFields.length > 0 ? formFields : undefined,
          };
        })
      );

      console.log("🎉 Todos os itens processados:", itemsWithValues);
      return itemsWithValues;
    },
    enabled: open && !!transaction?.id && items.length > 0,
  });

  // Enriquecer items com produtos e valores do formulário
  const enrichedItems = (itemsWithFormValues || items).map((item) => {
    const baseItem = itemsWithFormValues?.find((i: any) => i.id === item.id) || item;
    const enriched = {
      ...baseItem,
      product: products?.find((p) => p.id === item.product_id),
    };
    console.log("📦 Item enriquecido:", enriched.id, "form_values:", enriched.form_values, "form_fields:", enriched.form_fields);
    return enriched;
  });

  // Enriquecer payments com métodos de pagamento
  const enrichedPayments = payments.map((payment) => ({
    ...payment,
    payment_method: paymentMethods?.find((pm) => pm.id === payment.payment_method_id),
  }));

  const enrichedTenant = tenantData || tenant;

  // Função auxiliar para formatar valor do campo do formulário
  const normalizeType = (t: string) => {
    const s = (t || "").toLowerCase();
    if (["seleção única", "selecao unica", "select"].includes(s)) return "select";
    if (["múltipla seleção", "multipla selecao", "multiselect"].includes(s)) return "multiselect";
    if (["radio"].includes(s)) return "radio";
    if (["checkbox"].includes(s)) return "checkbox";
    if (["arquivos", "arquivo", "files", "file"].includes(s)) return "file";
    if (["imagens", "imagem", "images", "image"].includes(s)) return "image";
    return s;
  };

  const formatFormFieldValue = (field: any, value: any): string => {
    if (!value && value !== 0) return "";

    // Parse JSON string arrays
    if (typeof value === "string") {
      const trimmed = value.trim();
      if ((trimmed.startsWith("[") && trimmed.endsWith("]")) || (trimmed.startsWith("{") && trimmed.endsWith("}"))) {
        try {
          const parsed = JSON.parse(trimmed);
          value = parsed;
        } catch {}
      }
    }

    switch (normalizeType(field.tipo)) {
      case "select":
      case "radio":
        // Buscar opção pelo valor
        const option = field.form_field_options?.find((opt: any) => opt.id === value || opt.valor === value);
        return option ? option.label : String(value);
      
      case "multiselect":
      case "checkbox":
        // Array de valores
        if (Array.isArray(value)) {
          return value
            .map((v) => {
              const opt = field.form_field_options?.find((opt: any) => opt.id === v || opt.valor === v);
              return opt ? opt.label : String(v);
            })
            .join(", ");
        }
        return String(value);
      
      case "file":
      case "image":
        if (value && typeof value === "object") {
          const name = (value as any).original_name || (value as any).path || "arquivo";
          return name;
        }
        return String(value);
      
      default:
        return String(value);
    }
  };

  const handlePrintA4 = async () => {
    await generateA4PDF({
      transaction,
      items: enrichedItems,
      payments: enrichedPayments,
      client,
      seller,
      tenant: enrichedTenant,
    });
  };

  const handlePrintTicket = async () => {
    await generateTicketPDF({
      transaction,
      items: enrichedItems,
      payments: enrichedPayments,
      client,
      seller,
      tenant: enrichedTenant,
    });
  };

  // Calcular totais
  const subtotal = enrichedItems.reduce((sum, item) => sum + item.total, 0);
  const desconto = transaction?.desconto || 0;
  const total = subtotal - desconto;
  const totalPago = enrichedPayments
    .filter((p) => p.status === "confirmado")
    .reduce((sum, p) => sum + p.valor, 0);
  const saldo = total - totalPago;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b sticky top-0 bg-background z-10 relative">
          <DialogTitle>Visualização para Impressão</DialogTitle>
          <div className="absolute right-12 top-0 -translate-y-0">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Printer className="h-4 w-4" />
                  Imprimir
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={handlePrintA4}>
                  <FileDown className="h-4 w-4 mr-2" />
                  A4
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handlePrintTicket}>
                  <FileDown className="h-4 w-4 mr-2" />
                  Ticket
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="bg-white p-8 shadow-lg">
            {/* Header */}
            <div className="text-center mb-4 border-b pb-2">
              <h1 className="text-2xl font-bold">{enrichedTenant?.name || "Empresa"}</h1>
              {enrichedTenant?.email && <p className="text-sm text-muted-foreground mt-1">{enrichedTenant.email}</p>}
            </div>

            {/* Informações da Venda */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-2">Informações da Venda</h2>
              <div className="space-y-3 text-sm">
                {/* Primeira linha: Número (esquerda), Data da Venda (centro), Data de Entrega (direita) */}
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-bold">Número:</span>
                    <span className="ml-3">{transaction?.id?.substring(0, 8) || "N/A"}</span>
                  </div>
                  <div className="flex-1 text-center">
                    <span className="font-bold">Data da Venda:</span>
                    <span className="ml-3">
                      {transaction?.data
                        ? new Date(transaction.data).toLocaleDateString("pt-BR")
                        : new Date().toLocaleDateString("pt-BR")}
                    </span>
                  </div>
                  {transaction?.data_entrega && (
                    <div className="text-right">
                      <span className="font-bold">Data de Entrega:</span>
                      <span className="ml-3">
                        {new Date(transaction.data_entrega).toLocaleDateString("pt-BR")}
                      </span>
                    </div>
                  )}
                </div>

                {/* Segunda linha: Cliente (esquerda), Telefone (centro), Email (direita) */}
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-bold">Cliente:</span>
                    <span className="ml-3">{client?.nome || "N/A"}</span>
                  </div>
                  {client?.telefone && (
                    <div className="flex-1 text-center">
                      <span className="font-bold">Telefone:</span>
                      <span className="ml-3">{client.telefone}</span>
                    </div>
                  )}
                  {client?.email && (
                    <div className="text-right">
                      <span className="font-bold">Email:</span>
                      <span className="ml-3">{client.email}</span>
                    </div>
                  )}
                </div>

                {/* Terceira linha: Vendedor */}
                <div>
                  <span className="font-bold">Vendedor:</span>
                  <span className="ml-3">{seller?.nome_completo || "N/A"}</span>
                </div>

                {transaction?.descricao && (
                  <div>
                    <span className="font-bold">Descrição:</span>
                    <span className="ml-3">{transaction.descricao}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Itens */}
            <div className="mb-6">
              <h2 className="text-lg font-semibold mb-4">Itens da Venda</h2>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2">Produto</th>
                    <th className="text-right p-2">Qtd</th>
                    <th className="text-right p-2">Preço Unit.</th>
                    <th className="text-right p-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {enrichedItems.map((item, index) => {
                    const hasFormValues = item.form_fields && item.form_fields.length > 0 && item.form_values && 
                      item.form_fields.some((field: any) => 
                        item.form_values?.[field.slug] !== undefined && 
                        item.form_values?.[field.slug] !== null && 
                        item.form_values?.[field.slug] !== ""
                      );
                    
                    const formFieldsWithValues = hasFormValues 
                      ? item.form_fields.filter((field: any) => 
                          item.form_values?.[field.slug] !== undefined && 
                          item.form_values?.[field.slug] !== null && 
                          item.form_values?.[field.slug] !== ""
                        )
                      : [];
                    
                    return (
                      <React.Fragment key={index}>
                        {index > 0 && (
                          <>
                            <tr>
                              <td className="pb-0.5" colSpan={4}></td>
                            </tr>
                            <tr className="border-b border-gray-200">
                              <td className="pb-0.5" colSpan={4}></td>
                            </tr>
                            <tr>
                              <td className="pt-0.5" colSpan={4}></td>
                            </tr>
                          </>
                        )}
                        <tr>
                          <td className="p-2 pb-0">{item.product?.nome || "Produto"}</td>
                          <td className="text-right p-2 pb-0">{item.quantidade}</td>
                          <td className="text-right p-2 pb-0">
                            {new Intl.NumberFormat("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            }).format(item.preco_unitario)}
                          </td>
                          <td className="text-right p-2 pb-0">
                            {new Intl.NumberFormat("pt-BR", {
                              style: "currency",
                              currency: "BRL",
                            }).format(item.total)}
                          </td>
                        </tr>
                        {hasFormValues && (
                          <tr>
                            <td className="p-2 pl-4 pt-0 pb-4" colSpan={4}>
                              <div className="text-sm text-muted-foreground flex flex-wrap gap-3">
                                {formFieldsWithValues.map((field: any, fieldIndex: number) => {
                                  const value = item.form_values[field.slug];
                                  const formattedValue = formatFormFieldValue(field, value);
                                  const isObj = value && typeof value === "object";
                                  const signedUrl = isObj ? (value as any).signed_url : null;
                                  const isImage = field.tipo === "image" || (isObj && (((value as any).mime_type || "").startsWith("image/")));
                                  return (
                                    <span key={field.id} className="flex items-center gap-2">
                                      <span className="font-bold">{field.nome}:</span>{" "}
                                      {isImage && signedUrl ? (
                                        <img src={signedUrl} alt={formattedValue} className="h-8 w-auto rounded border" />
                                      ) : (
                                        <span>{formattedValue}</span>
                                      )}
                                      {field.tipo === "file" && signedUrl && (
                                        <a href={signedUrl} target="_blank" rel="noreferrer" className="text-blue-600 underline">
                                          abrir
                                        </a>
                                      )}
                                      {fieldIndex < formFieldsWithValues.length - 1 && <span className="mx-1">•</span>}
                                    </span>
                                  );
                                })}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagamentos */}
            {payments.length > 0 && (
              <div className="mb-6">
                <h2 className="text-lg font-semibold mb-4">Pagamentos</h2>
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Data</th>
                      <th className="text-left p-2">Método</th>
                      <th className="text-right p-2">Valor</th>
                      <th className="text-left p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrichedPayments.map((payment, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-2">
                          {(() => {
                            const paymentDate = payment.data || payment.confirmed_at || payment.created_at;
                            return paymentDate
                              ? new Date(paymentDate).toLocaleDateString("pt-BR")
                              : "N/A";
                          })()}
                        </td>
                        <td className="p-2">{payment.payment_method?.nome || "N/A"}</td>
                        <td className="text-right p-2">
                          {new Intl.NumberFormat("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          }).format(payment.valor)}
                        </td>
                        <td className="p-2">
                          <span
                            className={
                              payment.status === "confirmado"
                                ? "text-green-600"
                                : "text-yellow-600"
                            }
                          >
                            {payment.status === "confirmado" ? "Confirmado" : "Pendente"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totais */}
            <div className="border-t pt-4">
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>
                      {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(subtotal)}
                    </span>
                  </div>
                  {desconto > 0 && (
                    <div className="flex justify-between">
                      <span>Desconto:</span>
                      <span>
                        {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(desconto)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total:</span>
                    <span>
                      {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(total)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total Pago:</span>
                    <span>
                      {new Intl.NumberFormat("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                      }).format(totalPago)}
                    </span>
                  </div>
                  {saldo > 0 && (
                    <div className="flex justify-between text-red-600 font-semibold">
                      <span>Saldo:</span>
                      <span>
                        {new Intl.NumberFormat("pt-BR", {
                          style: "currency",
                          currency: "BRL",
                        }).format(saldo)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-4 border-t text-center text-xs text-muted-foreground">
              <p>{enrichedTenant?.name || "Empresa"}</p>
              {enrichedTenant?.email && <p>{enrichedTenant.email}</p>}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

